<template>
  <div class="raster-type-control">
    <div v-if="visibleCogs.length === 0" class="no-data">
      No data{{ selectedYears.size > 0 ? ' for selected years' : '' }}.
    </div>
    <div
      v-for="cog in visibleCogs"
      :key="cog.id"
      class="cog-row"
    >
      <label class="cog-label">
        <!-- Exclusive scopes share one native radio name so the browser also
             clears the previous pick across sibling nodes and sub-groups.
             Non-exclusive scopes keep several nodes active at once, so their
             date radios must stay grouped per node. -->
        <input
          v-sync-checked="isNodeActive && selectedCogByNode.get(node.id) === cog.id"
          type="radio"
          class="cog-radio"
          :name="scope.exclusive ? `scope-${scope.scopeId}` : `raster-type-${node.id}`"
          :value="cog.id"
          @change="onSelectCog(cog.id)"
        />
        <span class="cog-date">{{ formatDate(cog.sensing_date) }}</span>
      </label>
      <div class="cog-actions">
        <button
          class="action-btn"
          :class="{ 'action-btn--loading': zoomingCogId === cog.id }"
          :disabled="zoomingCogId === cog.id"
          title="Ugrás a réteghez"
          @click.stop="zoomToLayer(cog)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="7"/>
            <line x1="11" y1="8" x2="11" y2="14"/>
            <line x1="8" y1="11" x2="14" y2="11"/>
            <line x1="20" y1="20" x2="16.5" y2="16.5"/>
          </svg>
        </button>
        <button
          class="action-btn"
          :class="{ 'action-btn--active': isSwipeTarget(cog.id) }"
          :title="isSwipeTarget(cog.id) ? 'Összehasonlító réteg eltávolítása' : 'Összehasonlító rétegnek beállít'"
          @click.stop="toggleSwipe(cog)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="3" x2="12" y2="21"/>
            <polyline points="7 8 4 11 7 14"/>
            <polyline points="17 8 20 11 17 14"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import api from '@/api/client'
import { useLayerSelection } from '@/composables/useLayerSelection'
import type { SelectionScope } from '@/composables/layerScope'
import { vSyncChecked } from '@/directives/syncChecked'
import type { LayerTreeRasterTypeNode, LayerTreeCOG } from '@/types/layerTree'

interface COGBounds {
  bounds: [number, number, number, number]  // EPSG:3857
}

const props = defineProps<{
  node: LayerTreeRasterTypeNode
  /** Selection scope this node belongs to (see composables/layerScope.ts). */
  scope: SelectionScope
}>()

const {
  selectedCogByNode,
  swipeTarget,
  selectedYears,
  selectCog,
  activateNode,
  isNodeActive: isNodeActiveFn,
  setSwipeTarget,
  zoomToExtent,
} = useLayerSelection()

const isNodeActive = computed(() => isNodeActiveFn(props.scope.scopeId, props.node.id))

const zoomingCogId = ref<number | null>(null)

const visibleCogs = computed(() => {
  if (!selectedYears.value.size) return props.node.cogs
  return props.node.cogs.filter(c => selectedYears.value.has(new Date(c.sensing_date).getFullYear()))
})

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('hu-HU', {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

// Picking a date always activates the node — never toggles it off. A radio only
// fires `change` when the selection actually moves, so a repeat activation here
// always means "the user picked a different date of an already-active node".
// Letting that fall through to activateNode's toggle-off branch deactivated the
// node and left the new date's COG off the map.
function onSelectCog(cogId: number) {
  activateNode(props.scope.scopeId, props.node.id, props.scope.exclusive, false)
  selectCog(props.node.id, cogId)
}

function isSwipeTarget(cogId: number): boolean {
  return swipeTarget.value?._key === `cog-${cogId}`
}

function toggleSwipe(cog: LayerTreeCOG) {
  if (isSwipeTarget(cog.id)) {
    setSwipeTarget(null)
  } else {
    const style = props.node.raster_type.titiler_style_string
    const url = style ? `${cog.tile_url}?${style}` : cog.tile_url
    setSwipeTarget({ _key: `cog-${cog.id}`, isCog: true, layer_type: 'xyz', url, opacity: 1, ordering: 9000 })
  }
}

async function zoomToLayer(cog: LayerTreeCOG) {
  zoomingCogId.value = cog.id
  try {
    const res = await api.get<COGBounds>(`/map/tiles/${cog.id}/bounds/`)
    zoomToExtent(res.data.bounds)
  } catch (e) {
    console.error('Failed to fetch COG bounds', e)
  } finally {
    zoomingCogId.value = null
  }
}
</script>

<style scoped>
.raster-type-control { display: flex; flex-direction: column; gap: 2px; padding: 2px 0; }
.no-data { font-size: 11px; color: #585b70; padding: 4px 0; }

.cog-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 4px;
  padding: 2px 4px;
  transition: background 0.1s;
}
.cog-row:hover { background: rgba(137,180,250,0.07); }

.cog-label {
  display: flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  flex: 1;
}
.cog-radio { accent-color: #a6e3a1; cursor: pointer; }
.cog-date { font-size: 12px; color: #cdd6f4; }

.cog-actions { display: flex; gap: 4px; }

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: none;
  border: 1px solid #313244;
  border-radius: 6px;
  color: #6c7086;
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s, background 0.12s;
}
.action-btn:hover:not(:disabled) { color: #89b4fa; border-color: #89b4fa; background: rgba(137,180,250,0.1); }
.action-btn--active {
  color: #a6e3a1 !important;
  border-color: #a6e3a1 !important;
  background: rgba(166,227,161,0.12) !important;
}
.action-btn--loading { opacity: 0.5; cursor: wait; }
</style>
